import { examples } from '@/api/chatGPT/examples'
import dataHelpers from '@/helpers/dataHelpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'

export const promptHelpers = {
  actor (systemPrompt = false) {
    let res = ''
    if (!systemPrompt) {
      res += 'Actor:\n'
    }
    res += 'You are an expert in Event Sourcing, Event Modeling, and Event Storming, with a strong background in Domain-Driven Design (DDD). You analyze business processes or project scopes to identify relevant state-changing domain events and organize them on a timeline, providing a coherent story of the system\'s behavior. You identify roles (human actors and automated systems), the commands they issue, and the read models and commands required. You also suggest workflow optimizations and best practices in systems architecture.'
    if (!systemPrompt) {
      res += '\n\n'
    }
    return res
  },
  oldVersionActor (description) {
    // Used in case of OpenAI version 3.5
    return `You are an expert in Event Sourcing, Event Modeling, and Event Storming, with a strong background in Domain-Driven Design (DDD). You analyze business processes or project scopes to identify relevant state-changing domain events and organize them on a timeline, providing a coherent story of the system's behavior. You identify roles (human actors and automated systems), the commands they issue, and the read models and commands required. You also suggest workflow optimizations and best practices in systems architecture.

**Workflow Description:**
\`${description}\`

**Instructions:**
- Generate limited number of state-changing events in chronological order based on the Workflow Description.
- Ensure that each event is unique and does not repeat.
- Arrange events logically from start to end, with no backtracking or looping.
- Always provide the response in valid JSON format without additional explanatory text.
- If any details are unclear or missing, make reasonable assumptions based on your expertise. Do not ask for clarifications.
- Follow the JSON examples that will be provided in the user messages.`
  },
  requestAllCardsForEachStep (workflow, requirementTypes) {
    let res = ''
    res += '**Request:**\n'
    res += this.cardTypeInstruction(requirementTypes)
    res += this.importantConsideration(requirementTypes, workflow.eventsJson.length)
    res += this.formatInstruction(workflow, requirementTypes, {
      step: false, shouldAddCards: true, shouldAddDataModels: false
    })
    return res
  },
  requestDataModelsForEachStep (workflow, generateCommand, requirementTypes) {
    let res = ''

    if (workflow.dataModels?.length) {
      res += this.commandsFromEntitiesInstructions(workflow.dataModels)
    }

    res += '**Request:**\n'
    res += this.writeModelInstruction(generateCommand, 'create-workflow')
    res += this.formatInstruction(workflow, requirementTypes, {
      step: false, shouldAddCards: false, shouldAddDataModels: true
    })
    return res
  },
  requestReadModelsForEachStep (workflow, generateReadModel, requirementTypes) {
    let res = ''

    res += '**Request:**\n'
    res += this.readModelInstruction(generateReadModel, 'create-workflow')
    res += this.formatInstruction(workflow, requirementTypes, {
      step: false, shouldAddCards: false, shouldAddDataModels: true
    })
    return res
  },
  backgroundCurrentDiagram (description, workflow, requirementTypes) {
    let content = '**Background - Workflow Description (business process, workflow or project scope):**\n'
    content += `${description}\n\n`

    content += '**Background workflow (JSON format):**\n'
    if (workflow) {
      content += '```json\n'
      content += JSON.stringify(workflow)
      content += '\n```\n\n'

      content += '**Clarification:**\n'
      content += 'Each step in the workflow JSON contains the step number, the role and the description.'
      content += this.metaDataInstruction(requirementTypes)
    } else {
      content += 'Workflow not defined.\n\n'
    }

    return content
  },
  metaDataInstruction (requirementTypes) {
    let foundType = false
    let res = ' Each step (in the workflow) can also include metadata with the following meanings:\n'
    for (const requirementType of requirementTypes) {
      if (requirementType.generateWithAI) {
        res += `${this.formatCardKey(requirementType.title)} : ${requirementType.description}\n`
        foundType = true
      }
    }
    res += '\n'
    if (!foundType) {
      res = ''
    }
    return res
  },
  writeModelInstruction (generateCommand, mode = 'create-workflow', command = undefined, aggregate = undefined) {
    let res = ''
    if (generateCommand) {
      let createASetOf
      let simpleInputForms
      let steps
      if (mode === 'add-shape') {
        createASetOf = 'Also create a set of'
        simpleInputForms = 'a simple input form (or mockup or wireframe)'
        steps = 'for this step of the process'
      } else if (mode === 'create-workflow') {
        createASetOf = 'For each step, create a set of'
        simpleInputForms = 'simple input forms (or mockups or wireframes)'
        steps = 'for each step of the process'
      } else if (mode === 'create-event-story') {
        createASetOf = 'For this step in the process, create a set of'
        simpleInputForms = 'a simple input form (or mockup or wireframe)'
        steps = 'for this step of the process'
      }
      res = `${createASetOf} input fields. You are designing ${simpleInputForms} with data fields to be filled in and submitted by the actor (or role), `
      if (command && aggregate) {
        res += `in order to invoke the command "${command}" on the aggregate "${aggregate}", ${steps}. `
      } else {
        res += `in order to invoke a command on an aggregate, ${steps}. `
      }
      res += 'Each data field is an input field that the user (or system, if automated) can fill in. The form reflects the information requested from the actor in order to invoke the command on the aggregate. '
      res += 'The number of input fields per step can vary, but it is usually between 4 and 8. '
      res += 'For each input field: Create a list of 3 items with typical example data. Tag each data field with REQUIRED if it should be a required field in the input form. '
      res += 'A data field name, along with the example data value, must each be at most 30 characters long.\n\n'
    }
    return res
  },
  readModelInstruction (generateReadModel, mode = 'create-workflow') {
    let res = ''
    if (generateReadModel) {
      let createASetOf
      let simpleInputForms
      let steps
      if (mode === 'add-shape') {
        createASetOf = 'Also create a set of'
        simpleInputForms = 'a simple read model (or projections)'
        steps = 'for this step of the process'
      } else if (mode === 'create-workflow') {
        createASetOf = 'For each step, create a set of'
        simpleInputForms = 'simple read models (or projections)'
        steps = 'for each step of the process'
      }
      res = `${createASetOf} input fields. You are designing ${simpleInputForms} with data fields to be filled in and submitted by the actor (or role), `
      res += `in order to support querying, ${steps}. `
      res += 'Each data field is part of a denormalized view that the user (or system, if automated) can read efficiently. The read model reflects the information needed by the actor at this stage of the process. '
      res += 'The number of input fields per step can vary, but it is usually between 4 and 8. '
      res += 'For each input field: Create a list of 3 items with typical example data. '
      res += 'Tag each data field with INDEXED if it should be indexed for filtering or search. '
      res += 'A data field name, along with the example data value, must each be at most 30 characters long.\n\n'
    }
    return res
  },
  readModelBackgroundAndInstruction (aiDescription, roleName, description, eventId, svgGraph) {
    let res = ''
    res += 'Background:\n'
    res += `In a workflow containing '${aiDescription}' there is one business event defined as: '${roleName} - ${description}'. `
    res += 'The relevant data types/entities, that are either provided or captured in connection to this business event are: '
    const index = svgGraph[eventId].dataModels.findIndex(model => model.type === 'Event Story')
    const fields = svgGraph[eventId].dataModels[index].dataFields
    res += '[' + fields.map(field => field.name).join(', ') + ']\n\n'
    res += 'Request:\n'
    res += 'Create an example of a Read Model based on the data types/entities listed above, optimized for querying in views related to this business event. '
    res += 'The Read Model should include relevant fields and attributes, structured to allow efficient retrieval for typical queries. '
    res += 'Provide 3 rows of example data for each field.'
    return res
  },
  cardTypeInstruction (cardTypes) {
    let res = `For each step, add one item of each type from the following list:
`
    for (const cardType of cardTypes) {
      if (cardType.generateWithAI) {
        res += `- ${this.adjustEnding(cardType.title, 1)}: ${this.removeTrailingDot(cardType.description)} (as ${this.formatCardKey(cardType.title)}).
`
      }
    }
    if (res !== '') {
      res += '\n'
    }
    return res
  },
  cardTypeInstruction2 (requirementType, addShape = false, content) {
    let res = ''
    if (requirementType.generateWithAI) {
      if (content.slice(-1) === '.') {
        res += ' '
      }
      res += `Also add one ${this.adjustEnding(requirementType.title, 1)}: ${this.removeTrailingDot(requirementType.description)} (as ${this.formatCardKey(requirementType.title)}).`
    }
    return res
  },
  createExampleObject (workflow, requirementTypes, step = null, { role, description, dataFieldsExample }, shouldAddCards = true, shouldAddDataModels = true) {
    const exampleObj = {}
    exampleObj.role = role
    exampleObj.description = description
    if (step) {
      exampleObj.step = 0.5 + step
    }
    if (shouldAddDataModels && workflow.generateCommand) {
      exampleObj.dataFields = dataFieldsExample
    }
    if (shouldAddCards) {
      for (let i = 0; i < requirementTypes?.length; i++) {
        if (requirementTypes[i].generateWithAI) {
          const key = this.formatCardKey(requirementTypes[i].title)
          exampleObj[key] = []
          // TODO: remove loop because it just loops 1 time
          for (let j = 0; j < 1; j++) {
            if (j === 0) {
              exampleObj[key].push(`Example description ${requirementTypes[i].title.toLowerCase()} X`)
            }
          }
        }
      }
    }
    return exampleObj
  },
  formatInstruction (workflow, requirementTypes, formatOptions) {
    const { step, shouldAddCards, shouldAddDataModels } = formatOptions
    let content = ''
    if (!step) {
      // send 2 example objects
      content += '**Formatting**:\n'
      content += `The result should be formatted as a valid JSON array similar to this example (but with ${workflow.eventsJson.length} items):\n\n`
      content += '```json\n'
      const exampleObj1 = this.createExampleObject(workflow, requirementTypes, step, { role: 'Role description', description: 'Created order', dataFieldsExample: examples.dataFieldsExampleCreateOrder }, shouldAddCards, shouldAddDataModels)
      const exampleObj2 = this.createExampleObject(workflow, requirementTypes, step, { role: 'Role description', description: 'Resolved issue', dataFieldsExample: examples.dataFieldsExampleDeliverOrder }, shouldAddCards, shouldAddDataModels)
      content += JSON.stringify([exampleObj1, exampleObj2])
      content += '\n```\n\n'
    } else {
      // sent only 1 example object
      content += '**Formatting**:\n'
      content += 'Don\'t return all the steps. Only return the new step. The result should be formatted as a valid JSON array of size 1, containing only one object similar to this example:\n\n'
      content += '```json\n'
      const exampleObj1 = this.createExampleObject(workflow, requirementTypes, step, { role: 'Role X', description: 'Approved invoice', dataFieldsExample: examples.dataFieldsExampleCreateOrder })
      content += JSON.stringify([exampleObj1])
      content += '\n```\n\n'
    }
    return content
  },
  importantConsideration (requirementTypes, eventsCount) {
    let res = ''
    res += '**Very Important Consideration**:\n'
    res += `It is crucial to ensure that the output contains exactly ${eventsCount} JSON objects (one for each event). Each JSON object must strictly adhere to the following structure and item count:\n`
    res += requirementTypes.filter(r => r.generateWithAI)
      .map(r => `${r.title}: Exactly one item`).join('\n')
    res += '\n\n'
    return res
  },
  entitiesFromWorkflowInstructions () {
    return `**Important Instructions:**
Explicit Field Inclusion:
For every command that targets an aggregate root, include all the command arguments (input fields) in the corresponding entity. Even if a field appears only in one command (e.g., "Special Instructions" in the "Create Order" command), it must be included in the aggregate root.

Merge command Fields:
When an aggregate root is involved in multiple commands, assemble all unique input arguments (data fields) from the associated commands. The resulting entity should have a field for every attribute mentioned in any of the commands.

Step-by-Step Directive:
1. Identify the input data or arguments for each command.
2. Determine which entity acts as the aggregate root for that command.
3. Extract all unique arguments from these commands and include them in the aggregate root's definition, even if some arguments appear in only one command.\n\n`
  },
  commandsFromEntitiesInstructions (entities) {
    if (!entities || entities.length === 0) {
      return ''
    }

    let content = ''
    content += '**Background - Commands with Entity Mapping:**\n'
    content += 'In the workflow, commands operate on aggregate roots. '
    content += 'When a "List of available entities" is provided, you must analyze the entities and, for each command, determine which entity best fits as the aggregate root. '
    content += 'If an entity is deemed to be an aggregate root, all input fields (command arguments) must correspond to an existing data field in that entity or one of its related entities. This is to ensure that each input field has a destination in an entity.'
    content += '\n'

    content += this.backgroundEntities(entities)
    content += '\n'
    content += `**Important Instructions:**
For every command, compare its input fields with the fields of the existing entities. Only include those input fields that have a corresponding field in an entity. If an input field does not exist in an entity, omit it to ensure consistency.

Step-by-Step Directive:
1. Identify the input fields for each command.
2. Determine which entity from the "List of available entities" that is the best candidate for being the aggregate root for that command.
3. Compare the command's input fields to the entity that is the aggregate root, include only those fields that exist in the entity's definition or its related entities.\n\n`

    return content
  },
  adjustEnding (word, quant) {
    if (quant > 1 && word.charAt(word.length - 1) === 'y') {
      const str = word.substring(0, word.length - 1)
      return str + 'ies'
    }
    if (quant > 1 && word.charAt(word.length - 1) !== 's') {
      return word + 's'
    }
    if (quant === 1 && word.charAt(word.length - 1) === 's') {
      return word.substring(0, word.length - 1)
    }
    return word
  },
  removeTrailingDot (word) {
    if (word?.charAt(word.length - 1) === '.') {
      return word.substring(0, word.length - 1)
    }
    return word
  },
  diagramToJson (eventId, workflow, svgGraph, svgLanes, options) {
    let excludeCards = false
    if (options?.excludeCards) {
      excludeCards = true
    }
    let currentStep
    const res = []
    let index = 1
    const parentChildEventMap = dataHelpers.createMainParentChildEventMap(workflow.eventsJson)
    const eventArray = []
    let tmpEventId = eventId || workflow.eventsJson[0].id
    while (svgGraph[tmpEventId].parents[0] !== 'start') {
      eventArray.unshift(svgGraph[tmpEventId].id)
      tmpEventId = svgGraph[tmpEventId].parents[0]
    }
    eventArray.unshift(svgGraph[tmpEventId].id)
    tmpEventId = eventId || workflow.eventsJson[0].id
    while (parentChildEventMap[tmpEventId] || (workflow.eventsJson.findIndex(ev => ev.parents.includes(tmpEventId)) !== -1)) {
      if (parentChildEventMap[tmpEventId]) {
        eventArray.push(parentChildEventMap[tmpEventId][0].id)
        tmpEventId = parentChildEventMap[tmpEventId][0].id
      } else {
        const indexEvent = workflow.eventsJson.findIndex(wf => wf.parents.includes(tmpEventId))
        eventArray.push(workflow.eventsJson[indexEvent].id)
        tmpEventId = workflow.eventsJson[indexEvent].id
      }
    }
    let previousEvent = null
    for (const eventId2 of eventArray) {
      const event = svgGraph[eventId2]
      const cards = {}
      if (!excludeCards && event.requirementsJson) {
        for (const card of event.requirementsJson) {
          if (!card.cardType) continue
          const key = this.formatCardKey(card.cardType.title)
          if (!cards[key]) { cards[key] = [] }
          cards[key].push(card.description)
        }
      }
      if (previousEvent?.type === 'bpmn:ExclusiveGateway') {
        const index = event.conditionLabels?.findIndex(label => label.parentId === previousEvent.id)
        if (index > -1) {
          res[res.length - 1].description += ` : ${event.conditionLabels[index].name}`
        }
      }
      res.push({
        step: index,
        role: svgLanes[event.laneId].name,
        description: event.name,
        ...cards
      })
      if (event.id === eventId) {
        currentStep = index
      }
      previousEvent = event
      index++
    }
    return { workflow: res, currentStep }
  },
  descriptionAddOneStep (generateCommand, requirementTypes) {
    let addingCards = false
    let res = ''
    res += promptHelpers.writeModelInstruction(generateCommand, 'add-shape')
    for (let i = 0; i < requirementTypes.length; i++) {
      res += promptHelpers.cardTypeInstruction2(requirementTypes[i], true, res)
      if (requirementTypes[i].generateWithAI) {
        addingCards = true
      }
    }
    if (addingCards) {
      res += '\n\n'
    }
    return res
  },
  createExampleObjectCard (index, requirementTypes) {
    const obj = {}
    const key = this.formatCardKey(requirementTypes[index].title)
    obj[key] = 'Example text'
    return obj
  },
  formatCardKey (cardTitle) {
    // remove slashes and replace spaces with dashes
    return cardTitle.replace(/\//g, '').replace(/\s+/g, '-').toLowerCase()
  },
  backgroundRoleCommandAggregate (currentStep, roleName, description, commandName, aggregateName) {
    let content = ''
    content += '\n**Background Actor, Command, Aggregate:**\n'
    content += `The selected step in the workflow that we are working with right now is step: ${currentStep}: ${roleName} - ${description}. `
    if (commandName && aggregateName) {
      content += `This step involves the role (or actor) "${roleName}" invoking the command "${commandName}" on the aggregate "${aggregateName}".\n`
    } else {
      content += `This step involves the role (or actor) "${roleName}" invoking a command on an aggregate (create, update or delete).\n`
    }
    return content
  },
  backgroundWriteModel (currentStep, roleName, description, writeModelFields) {
    let content = '\n**Background on the Input Form:**\n'
    content += `Each step of the process should be seen as an actor (human or system) invoking a command on an aggregate. In order to perform that command, parameters (or arguments) are needed. Here is a JSON object representing the input form (UX wireframe or mockup) for step ${currentStep}, ${roleName} - ${description}:\n`
    content += '```json\n'
    content += JSON.stringify(writeModelFields)
    content += '\n```\n\n'
    return content
  },
  backgroundReadModel (currentStep, roleName, description, readModelFields) {
    let content = '\n**Background on the Read Model:**\n'
    content += `The Read Model represents the projection of data that is optimized for querying and displaying information to users or systems. It is designed to provide a denormalized, query-friendly view of the data, derived from one or more events or aggregates. Each field in the read model corresponds to a piece of information required to fulfill a specific query or display need. Below is a JSON object describing the read model for the ${currentStep}, ${roleName} - ${description} view, which provides the key attributes and their context:\n`
    content += '```json\n'
    content += JSON.stringify(readModelFields)
    content += '\n```\n\n'
    return content
  },
  backgroundEntities (entities) {
    if (!entities?.length) {
      return ''
    }

    const mappedEntities = entities.map((entity) => ({
      name: entity.name,
      dataFields: entity.dataFields.map(({ name }) => name)
    }))

    let content = '\n**List of available entities:**\n'
    content += '```json\n'
    content += JSON.stringify(mappedEntities)
    content += '\n```\n'

    return content
  },
  backgroundEventDataModels (event, currentStepObject, requirementTypes) {
    let writeModelBackground = ''
    let readModelBackground = ''
    const roleName = currentStepObject?.role
    const description = currentStepObject?.description
    const writeModel = event.dataModels?.find((model) => model.type === 'Event Story')
    const readModel = event.dataModels?.find((model) => model.type === 'Read model')
    const aggregateName = this.getAggregateName(currentStepObject, requirementTypes)
    const commandName = this.getCommandName(currentStepObject, requirementTypes)

    const writeModelDataFields = writeModel ? writeModel.dataFields.map(({ name, exampleData }) => ({ name, exampleData })) || [] : null
    const readModelDataFields = readModel ? readModel.dataFields.map(({ name, exampleData }) => ({ name, exampleData })) || [] : null

    if (writeModelDataFields) {
      writeModelBackground += `Below is a JSON object representing the input form (UX wireframe or mockup) for step ${currentStepObject.step}, ${roleName} - ${description}.`
      if (commandName && aggregateName) {
        writeModelBackground += ` (The command is "${commandName}" and it's invoked on the aggregate root "${aggregateName}".)\n`
      } else {
        writeModelBackground += ` (The command that is invoked in the step ${currentStepObject.step}: ${roleName} - ${description}.)\n`
      }
      writeModelBackground += '```json\n'
      writeModelBackground += JSON.stringify(writeModelDataFields)
      writeModelBackground += '\n```\n\n'
    }

    if (readModelDataFields) {
      readModelBackground += `Below is a JSON object describing the read model for the ${currentStepObject.step}, ${roleName} - ${description} view, which provides the key attributes and their context:\n`
      readModelBackground += '```json\n'
      readModelBackground += JSON.stringify(readModelDataFields)
      readModelBackground += '\n```\n\n'
    }

    return [writeModelBackground, readModelBackground]
  },
  getCommandName (currentStepObject, requirementTypes) {
    const commandCardType = diagramHelpers.methods.getCommandCardType(requirementTypes)

    if (!commandCardType) {
      return ''
    }

    const commandCardKey = this.formatCardKey(commandCardType.title)
    return currentStepObject[commandCardKey]?.[0]?.replace(/<\/?p>/g, '') // remove p tags
  },
  getAggregateName (currentStepObject, requirementTypes) {
    const aggregateCardType = diagramHelpers.methods.getAggregateCardType(requirementTypes)

    if (!aggregateCardType) {
      return ''
    }

    const aggregateCardKey = this.formatCardKey(aggregateCardType.title)
    return currentStepObject[aggregateCardKey]?.[0]?.replace(/<\/?p>/g, '') // remove p tags
  }
}
